import { Form } from "antd";
import message from "components/CustomMessage";
import { isEmpty, map, split } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const CLOUD_ACCOUNT = "cloud_account";

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  type: CLOUD_ACCOUNT,
};

const Rapid7InsightCloudMappingForm = forwardRef((props, ref) => {
  const selectedNames = useRef({});
  const [form] = Form.useForm();
  const [integrationType, setIntegrationType] = useState(CLOUD_ACCOUNT);

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });

  const getExtraFieldsData = useCallback((config, index) => {
    const { cloudAccountId, application } = config;
    const configSelectedNames = selectedNames.current[index];
    if (cloudAccountId) {
      return {
        cloudAccountId: cloudAccountId.join(),
        cloudAccountName: configSelectedNames.join(),
      };
    }

    return {
      application: application.join(),
    };
  }, []);

  const fetchCloudAccountsorApplications = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        type: integrationType,
      });
    return {
      promise: promise
        .then((res) => res.projects)
        .catch(() =>
          message.error(
            `Problem fetching ${integrationType === CLOUD_ACCOUNT ? "Cloud Accounts" : "Applications"}`,
          ),
        ),
      cancel,
    };
  }, [props.toolName, props.loginConfigId, integrationType]);

  const onIntegrationTypeChange = useCallback(
    (value, field) => {
      setIntegrationType(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      const updatedConfiguration = [...configurations];

      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        integration: value,
        cloudAccountId: undefined,
        application: undefined,
      };

      form.setFieldsValue({ configurations: updatedConfiguration });
    },
    [form],
  );

  const extraFields = useMemo(
    () => [
      {
        name: "type",
        colSpan: 15,
        label: "Select Integration Type",
        type: FieldTypes.SELECT,
        options: [
          { label: "Cloud Account", value: CLOUD_ACCOUNT },
          { label: "Applications", value: "applications" },
        ],
        props: {
          onChange: onIntegrationTypeChange,
        },
        required: true,
      },
      ...(integrationType === CLOUD_ACCOUNT
        ? [
            {
              name: "cloudAccountId",
              colSpan: "12",
              type: FieldTypes.MULTI_SELECT,
              label: "Cloud Account",
              required: true,
              props: {
                api: fetchCloudAccountsorApplications,
                onChange: (value, field, selectedOptions) => {
                  //handling multiple mapping case using index
                  selectedNames.current[field.name] = map(
                    selectedOptions,
                    "name",
                  );
                },
              },
            },
          ]
        : [
            {
              name: "application",
              colSpan: "12",
              required: true,
              type: FieldTypes.MULTI_SELECT,
              label: "Applications",
              props: {
                api: fetchCloudAccountsorApplications,
              },
            },
          ]),
    ],
    [fetchCloudAccountsorApplications, integrationType, onIntegrationTypeChange],
  );

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { cloudAccountId, cloudAccountName, application } = config;
    selectedNames.current[0] = split(cloudAccountName, ",");
    return {
      cloudAccountId: split(cloudAccountId, ","),
      type: cloudAccountId ? CLOUD_ACCOUNT : "applications",
      application: !isEmpty(application) ? split(application, ",") : undefined,
    };
  }, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      deleteIconTooltip="Delete Configuraion"
      {...props}
    />
  );
});

export default Rapid7InsightCloudMappingForm;
